import apiClient, { getWithDedupe } from './client'

import type { PaginatedResponse, Persona, PersonaInput } from '@/types/gia'

interface PersonaFilters {
  page?: number
  page_size?: number
  tipo?: 'atendido' | 'trabajador' | 'voluntario' | string | string[]
  activo?: boolean | boolean[]
  centro?: number | number[]
  cee?: boolean
  diuresis_habilitada?: boolean
  q?: string
}

const normalizeFilters = (filters: PersonaFilters) => {
  const params: Record<string, any> = { ...filters }

  if (Array.isArray(filters.tipo)) {
    params.tipo = filters.tipo.length ? filters.tipo.join(',') : undefined
  }

  if (Array.isArray(filters.centro)) {
    params.centro = filters.centro.length ? filters.centro.join(',') : undefined
  }

  if (Array.isArray(filters.activo)) {
    if (filters.activo.length === 2) {
      params.activo = undefined
    } else if (filters.activo.length === 1) {
      params.activo = filters.activo[0]
    } else {
      params.activo = undefined
    }
  }

  if (filters.cee === true) {
    params.cee = '1'
  } else if (filters.cee === false) {
    params.cee = undefined
  }

  if (filters.diuresis_habilitada === true) {
    params.diuresis_habilitada = '1'
  } else if (filters.diuresis_habilitada === false) {
    params.diuresis_habilitada = undefined
  }

  return params
}

const personasApi = {
  list: async (filters: PersonaFilters = {}): Promise<PaginatedResponse<Persona>> => {
    const params = normalizeFilters(filters)

    return getWithDedupe<PaginatedResponse<Persona>>('/personas/', {
      params
    })
  },

  listAll: async (filters: Omit<PersonaFilters, 'page'> = {}): Promise<Persona[]> => {
    const personas: Persona[] = []
    let page = 1
    let hasMore = true

    while (hasMore) {
      const response = await apiClient.get<PaginatedResponse<Persona>>('/personas/', {
        params: { page_size: 200, ...filters, page }
      })

      personas.push(...response.data.results)
      hasMore = response.data.next !== null
      page += 1
    }

    return personas
  },

  get: async (id: number): Promise<Persona> => {
    const response = await apiClient.get<Persona>(`/personas/${id}/`)

    return response.data
  },

  create: async (data: PersonaInput): Promise<Persona> => {
    const response = await apiClient.post<Persona>('/personas/', data)

    return response.data
  },

  update: async (id: number, data: Partial<PersonaInput>): Promise<Persona> => {
    const response = await apiClient.put<Persona>(`/personas/${id}/`, data)

    return response.data
  },

  uploadPhoto: async (id: number, file: File): Promise<{ foto_perfil: string | null }> => {
    const formData = new FormData()

    formData.append('foto_perfil', file)

    const response = await apiClient.post<{ foto_perfil: string | null }>(`/personas/${id}/photo/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/personas/${id}/`)
  },

  accesoWeb: async (
    id: number,
    canal: 'email' | 'telefono'
  ): Promise<{ canal: 'email' | 'telefono'; enviado_a?: string; dni?: string; password_temporal?: string; expira_en_horas: number }> => {
    const response = await apiClient.post(`/personas/${id}/acceso-web/`, { canal })

    return response.data
  }
}

export default personasApi
